import ReactDOM from "react-dom/client";

import "./index.css";
import "./init";
import "./utils/sentry";
import "./components/Icons";
import App from "./App";
import "./i18n";
import {
  MOBILE_KEY,
  ACCESS_TOKEN_KEY,
  EXPIRES_IN_KEY,
  RETURN_TO_URL
} from "./utils/localStorage";
import * as jose from "jose";
import auth from "./utils/auth";
import { buildWebviewPath } from "./utils/buildWebviewPath";

async function bootstrapApp() {
  const params = getQueryParams();
  const {
    mobile,
    accessToken,
    sessionTransferToken,
    expiresIn,
    path: webviewPath,
    leaseId,
    propertyId
  } = params;
  let userId = params.userId;

  if (window.location.href.includes("/sso")) {
    if (!sessionTransferToken) {
      throw new Error("Session transfer token is required for SSO login.");
    }

    await auth.authorize(sessionTransferToken, {
      ...params
    });
  } else {
    // if it is a webview, construct the route and redirect
    if (mobile && accessToken) {
      await setAuthValuesInLocalStorage(expiresIn!, accessToken);

      const route = await buildWebviewPath(
        window.location.origin,
        userId,
        webviewPath,
        leaseId,
        accessToken,
        propertyId
      );

      window.location.replace(route);
    } else {
      const isAccessTokenPresent = !!localStorage.getItem(ACCESS_TOKEN_KEY);
      const pathname = window.location.pathname;
      if (
        !isAccessTokenPresent &&
        pathname &&
        !["/logout", "/callback"].includes(pathname) &&
        //this has to be separate to account for branded login pages
        !pathname.includes("/login")
      ) {
        const fullPath = `${pathname}${window.location.search}${window.location.hash}`;
        localStorage.setItem(RETURN_TO_URL, fullPath);
      }

      ReactDOM.createRoot(
        document.getElementById("root") as HTMLElement
      ).render(<App />);
    }
  }
}

bootstrapApp();

export type WebViewQueryParams = {
  mobile: boolean;
  accessToken: string | null;
  expiresIn: string | null;
  leaseId: string;
  path: string;
  userId: string;
  sessionTransferToken: string | null;
  propertyId?: string;
};

function getQueryParams(): WebViewQueryParams {
  const queryString: string = window.location.search;
  const params = new URLSearchParams(queryString);
  const mobile = params.get(MOBILE_KEY) === "true";
  const accessToken = params.get(ACCESS_TOKEN_KEY);
  const expiresIn = params.get(EXPIRES_IN_KEY);
  const leaseId = params.get("property_unit_lease_id") as string;
  const sessionTransferToken = params.get("session_transfer_token");
  const path = params.get("path") ?? "checklist";
  const propertyId = params.get("property_id") as string;

  let userId: string = "";

  if (accessToken) {
    const decodedToken = jose.decodeJwt(accessToken);
    userId = decodedToken["https://api.binway.com/livly_user_id"] as string;
  }

  return {
    mobile,
    accessToken,
    expiresIn,
    leaseId,
    path,
    userId,
    sessionTransferToken,
    propertyId
  };
}

async function setAuthValuesInLocalStorage(
  expiresIn: string,
  accessToken: string
) {
  return new Promise((resolve) => {
    setTimeout(() => {
      if (expiresIn) {
        localStorage.setItem(EXPIRES_IN_KEY, expiresIn);
      }
      localStorage.setItem(MOBILE_KEY, "true");
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);

      resolve(true);
    }, 0);
  });
}
